import { generateRandomHex, type RequestOptions } from '@nextgen-web-framework/core';

import {
  type AddToListEntryRequest,
  type CreateListRequest,
  type UpdateListRequest,
  type ListResponse,
  type UserListResponse,
  BlockAndPrivilegeResponse,
} from '../list';
import {
  getListsByType,
  createListsObj,
  updateListsObj,
  deleteListObj,
  addToListsObj,
  getListReqObj,
  ListId,
  EntryId,
  removeEntryListReqObj,
  changeListEntryReqObj,
  PAGE_SIZE,
  getListByHybrisListIdReqObj,
  getSponserBlockByAccountIdReqObj,
} from '../config';
import {
  ApiProjections,
  GetByListType,
  ProjectionReqPayloadType,
  ListIdReqPayloadType,
  GetDisplayedOnShopValue,
  userDetailsReqPayloadType,
} from '../../types';
import { UpdateListResponse } from '../list/models/UpdateListResponse';
import { UpdateListQtyPayloadType } from '../../types/myListTypes';
import { CustomRequestOptions, MultipleEntryIdReqPayloadType } from '../../types/cartTypes';
import { listRequestWithVersionFix } from '../../utils';

export const myListApi = {
  getListsByType(
    { type, projections = ApiProjections.DETAILED, page = '0' }: GetByListType & ProjectionReqPayloadType,
    options?: CustomRequestOptions,
  ) {
    const req: RequestOptions = { ...getListsByType };
    req.params = {
      listType: type,
      projections: projections,
      pageSize: PAGE_SIZE,
      pageNumber: page,
    };
    return handleRequest({ ...req }, options);
  },
  createList(payload: CreateListRequest, options?: CustomRequestOptions) {
    const req = { ...createListsObj };
    req.body = payload as unknown as BodyInit;
    req.headers = {
      'X-Idempotency-Key': generateRandomHex(20),
    };
    return handleRequest(req, options, undefined, true) as Promise<ListResponse>;
  },
  updateList(payload: UpdateListRequest[], options?: CustomRequestOptions) {
    const req = { ...updateListsObj };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options) as Promise<UpdateListResponse>;
  },
  deleteList(listId: string, options?: CustomRequestOptions) {
    const req = { ...deleteListObj };
    req.url = req.url.replace(ListId, listId);
    return handleRequest(req, options, {
      listId,
    }) as Promise<ListResponse>;
  },
  addToList(payload: Array<AddToListEntryRequest>, options?: CustomRequestOptions) {
    const req = { ...addToListsObj };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },
  getListsByUser(
    {
      displayedOnShop,
      projections = ApiProjections.DETAILED,
      userId,
      accountId,
      listType,
      page = '0',
      category,
    }: GetDisplayedOnShopValue & ProjectionReqPayloadType,
    options?: CustomRequestOptions,
  ) {
    const req: RequestOptions = { ...getListsByType };
    let params: Record<string, string> = {
      projections: projections,
      pageSize: PAGE_SIZE,
      pageNumber: page,
      category: category || '',
      ...(userId && { userId: userId }), // will be removed after BE code deployment
      ...(userId && { ownerUserId: userId }),
      ...(accountId && { ownerAccountId: accountId }),
    };

    if (listType) {
      params = {
        ...params,
        listType,
      };
    }

    if (displayedOnShop !== undefined) {
      req.params = {
        ...params,
        isDisplayedOnShop: String(displayedOnShop),
      };
    } else {
      req.params = params;
    }
    return handleRequest({ ...req }, options);
  },
  getListById(
    {
      listId,
      userId,
      accountId,
      projections = ApiProjections.DETAILED,
    }: ProjectionReqPayloadType & ListIdReqPayloadType & userDetailsReqPayloadType,
    options?: CustomRequestOptions,
  ) {
    const req: RequestOptions = { ...getListReqObj };
    req.url = req.url.replace(ListId, listId as string);
    req.params = {
      projections: projections,
      ...(userId && { ownerUserId: userId }),
      ...(accountId && { ownerAccountId: accountId }),
    };

    return handleRequest({ ...req }, options);
  },
  getListByHybrisListId(listId: string, profileId?: string, options?: CustomRequestOptions): Promise<ListResponse> {
    const req: RequestOptions = { ...getListByHybrisListIdReqObj };
    req.url = req.url.replace(ListId, listId as string);
    if (profileId) {
      req.url += `?partyId=${encodeURIComponent(profileId)}`;
    }
    return handleRequest({ ...req }, options);
  },
  removeEntryFromList(
    {
      listId,
      entryId,
      projections = ApiProjections.DETAILED,
    }: ProjectionReqPayloadType & ListIdReqPayloadType & MultipleEntryIdReqPayloadType,
    options?: CustomRequestOptions,
  ) {
    const req = { ...removeEntryListReqObj };
    req.url = req.url.replace(ListId, listId as string);
    req.url = req.url.replace(EntryId, (entryId ?? []).join(','));
    req.params = {
      projections: projections,
    };
    return handleRequest(req, options, {
      listId: listId as string,
    });
  },
  changeQtyForEntryFromList(
    { listId, entryId, projections = ApiProjections.DETAILED, payload }: UpdateListQtyPayloadType,
    options?: CustomRequestOptions,
  ) {
    const req = { ...changeListEntryReqObj };
    req.url = req.url.replace(ListId, listId as string);
    req.url = req.url.replace(EntryId, String(entryId));
    req.params = {
      projections: projections,
    };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options, {
      listId: listId as string,
    });
  },
  getAllListByUser({ projections = ApiProjections.FULL }: ProjectionReqPayloadType, options?: CustomRequestOptions) {
    const req: RequestOptions = { ...getListsByType };
    req.params = {
      projections,
    };

    return handleRequest(req, options);
  },

  getSponserBlockDetail(accountId: string, options?: CustomRequestOptions) {
    const req: RequestOptions = { ...getSponserBlockByAccountIdReqObj };
    req.params = {
      ...(accountId && { accountNumber: accountId }),
    };
    return handleRequest(req, options) as Promise<BlockAndPrivilegeResponse>;
  },
};
export type ListResponseType = UserListResponse & ListResponse;

//private
const handleRequest = async (
  req: RequestOptions,
  options?: CustomRequestOptions,
  additionInfo?: {
    listId: string;
  },
  skipIdempotency = false,
) => {
  if (options?.headers) {
    req.headers = options?.headers;
  }
  if (options?.baseURL) {
    req.url = `${options.baseURL}${req.url}`;
  }
  const response: ListResponseType = await listRequestWithVersionFix(
    req,
    {
      skipIdempotency,
    },
    additionInfo,
  );
  return Promise.resolve(response);
};

export default myListApi;
